import {
  Button,
  ButtonColor,
  ButtonSize,
  Checkbox,
  Combobox,
  Option,
  TextInput,
  Tooltip,
} from '@code/innovance-react-ui-kit';
import { yupResolver } from '@hookform/resolvers';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { Controller, FieldError, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { CustomerTaxInfo } from 'src/front/api/apiInstance';
import { CustomerTaxInfoFormatResponse, ReferenceDataType } from 'src/front/api/generated';

import infoIcon from 'src/front/assets/icon_info.svg';

import usePRM from 'src/front/common/hooks/usePRM';
import { isInvalidTaxNumber } from 'src/front/common/utils/errorUtils';
import { formatFreeText } from 'src/front/common/utils/formUtils';
import { TaxResidencySchema } from 'src/front/common/utils/validationSchemas';
import FormModal from 'src/front/components//FormModal';
import Markdown from 'src/front/components//Markdown';

import {
  ButtonRow,
  CheckboxContainer,
  FormElement,
  FormRow,
  ResetButton,
  Title,
} from 'src/front/components/FormStyles';
import { IS_PROVIDED } from 'src/front/constant';
import styled from 'styled-components';

const TooltipContainer = styled(FormElement)`
  margin-top: 16px;
`;

export interface TaxFormData {
  taxResidenceCountry: Option | null;
  hasTaxNumberProvided: Option | null;
  differentNumber: boolean;
  foreignTaxNumber: string;
  foreignTaxNumberId?: string;
  isNew?: boolean;
}

const defaultValues = {
  taxResidenceCountry: null,
  hasTaxNumberProvided: null,
  differentNumber: false,
  foreignTaxNumber: '',
};
interface Props {
  countries: Option[];
  onClose: () => void;
  submit: (data: TaxFormData) => void;
  formValues?: TaxFormData;
}

const TaxModal: FC<Props> = ({ formValues, countries, onClose, submit }) => {
  const { t } = useTranslation();
  const [formValidations, setFormValidations] = useState<CustomerTaxInfoFormatResponse>();

  const yesNoComboItems = usePRM(ReferenceDataType.ISPROVIDED);

  const {
    watch,
    register,
    errors,
    clearErrors,
    setError,
    setValue,
    control,
    handleSubmit,
    formState,
    reset,
  } = useForm<TaxFormData>({
    defaultValues: formValues || defaultValues,
    resolver: yupResolver(TaxResidencySchema, {
      abortEarly: false,
      context: { formValidations },
    }),
  });

  const registerFields = useCallback(() => {
    register({ name: 'hasTaxNumberProvided' });
    register({ name: 'differentNumber' });
    register({ name: 'foreignTaxNumber' });
  }, [register]);

  useEffect(() => {
    registerFields();
  }, [registerFields]);

  const getFormValidations = useCallback(async (countryCode: string): Promise<void> => {
    const { data } = await CustomerTaxInfo.getCustomerTaxInfoFormat({
      countryCode,
    });
    if (data.result === 'OK') {
      setFormValidations(data);
    }
  }, []);

  useEffect(() => {
    if (formValues?.taxResidenceCountry?.value) {
      getFormValidations(formValues?.taxResidenceCountry?.value);
    }
  }, [formValues, getFormValidations]);

  const handleComboChange = (option: Option | null): void => {
    setValue('hasTaxNumberProvided', option, { shouldValidate: true, shouldDirty: true });
    !option?.value && clearErrors('foreignTaxNumber');
    !option?.value && setValue('foreignTaxNumber', '');
  };
  const handleTextChange = (text: string): void =>
    setValue('foreignTaxNumber', text, {
      shouldValidate: formState.isSubmitted && !!text,
      shouldDirty: formState.isSubmitted || !!text,
    });

  const handleCountryChange = (onChange?: (country: Option | null) => void) => async (
    country: Option | null
  ) => {
    if (onChange) {
      setValue('hasTaxNumberProvided', null);
      setValue('differentNumber', false);
      setValue('foreignTaxNumber', '');
      onChange(country);
    }
    if (country) {
      const { data } = await CustomerTaxInfo.getCustomerTaxInfoFormat({
        countryCode: country?.value || '',
      });

      if (data.result === 'OK') {
        setFormValidations(data);
      }
    } else {
      setFormValidations(undefined);
    }
  };

  const onReset = (): void => {
    reset();
    registerFields();
  };
  const onSubmit = async (formData: TaxFormData): Promise<void> => {
    const { differentNumber, foreignTaxNumber, taxResidenceCountry } = formData;
    try {
      const { data } = await CustomerTaxInfo.validateCustomerTaxInfoFormat({
        differentNumber,
        foreignTaxNumber,
        taxResidenceCountry: taxResidenceCountry?.value || '',
      });

      if (data.result === 'OK') {
        await submit({
          ...formValues,
          ...formData,
        });
      }
    } catch (error) {
      if (isInvalidTaxNumber(error)) {
        setError('foreignTaxNumber', { type: 'required', message: error?.errorMessage });
      }
    }
  };

  const tooltipText = watch('differentNumber')
    ? formValidations?.analogueInnToolTip
    : formValidations?.innToolTip;

  return (
    <FormModal onClose={onClose} size="400px" zIndex={900}>
      <Title>
        <Trans i18nKey="cardEntry_taxInfo_addTaxResidency_title" />
      </Title>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="taxResidenceCountry"
              render={({ onChange, value }) => (
                <Combobox
                  errorMessage={t((errors?.taxResidenceCountry as FieldError)?.message as string)}
                  items={countries}
                  label={t('cardEntry_taxInfo_taxResidenceCountry_label')}
                  name="taxResidenceCountry"
                  onChange={handleCountryChange(onChange)}
                  value={value}
                  required
                  searchable
                />
              )}
            />
          </FormElement>
        </FormRow>
        {watch('taxResidenceCountry') && watch('taxResidenceCountry')?.value !== 'RU' ? (
          <>
            <FormRow>
              <FormElement>
                <Combobox
                  errorMessage={t((errors?.hasTaxNumberProvided as FieldError)?.message as string)}
                  items={yesNoComboItems}
                  label={t('cardEntry_taxInfo_iinnProvided_label')}
                  name="hasTaxNumberProvided"
                  onChange={handleComboChange}
                  value={watch('hasTaxNumberProvided')}
                  required
                />
              </FormElement>
              {watch('hasTaxNumberProvided')?.value === IS_PROVIDED.PROVIDED &&
              formValidations?.hasDifferentNumber ? (
                <CheckboxContainer>
                  <Checkbox
                    id="differentNumber"
                    label={t('cardEntry_taxInfo_iinnDifferentFormat_text')}
                    onChange={({ target }) => setValue('differentNumber', target.checked)}
                    value={watch('differentNumber')}
                  />
                </CheckboxContainer>
              ) : null}
            </FormRow>
            {watch('hasTaxNumberProvided')?.value === IS_PROVIDED.PROVIDED ? (
              <FormRow>
                <FormElement>
                  <TextInput
                    errorMessage={t(errors?.foreignTaxNumber?.message as string, {
                      min: watch('differentNumber')
                        ? formValidations?.analogueInnMinLength
                        : formValidations?.innMinLength,
                      max: watch('differentNumber')
                        ? formValidations?.analogueInnMaxLength
                        : formValidations?.innMaxLength,
                    })}
                    formatter={formatFreeText}
                    label={t('cardEntry_taxInfo_iinn_label')}
                    maxLength={
                      watch('differentNumber')
                        ? formValidations?.analogueInnMaxLength
                        : formValidations?.innMaxLength
                    }
                    name="foreignTaxNumber"
                    onChange={handleTextChange}
                    value={watch('foreignTaxNumber')}
                    required
                  />
                </FormElement>
                {tooltipText ? (
                  <TooltipContainer>
                    <Tooltip content={<Markdown text={tooltipText} />}>
                      <img alt="tooltip" src={infoIcon} />
                    </Tooltip>
                  </TooltipContainer>
                ) : null}
              </FormRow>
            ) : null}
          </>
        ) : null}
        <ButtonRow>
          {formState.isDirty ? (
            <ResetButton
              color={ButtonColor.Secondary}
              onClick={onReset}
              size={ButtonSize.Medium}
              text={t('general_components_reset_button')}
              type="reset"
            />
          ) : null}
          <Button
            color={ButtonColor.Primary}
            disabled={formState.isSubmitting}
            size={ButtonSize.Medium}
            text={t('general_save_button')}
            type="submit"
          />
        </ButtonRow>
      </form>
    </FormModal>
  );
};

export default TaxModal;
